// Each inferred type sits under the schema it derives from — the type IS the schema.
import { z } from 'zod';

import { DocumentTemplateElementType, DocumentTemplateShapeType } from './constants';

// All coordinates are millimetres, top-left origin.
const textStyleSchema = z.object({
	fontSizePt: z.number().positive(),
	fontColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
	// Legacy paragraph-level styling; new rows carry these per-run instead.
	bold: z.boolean().optional(),
	italic: z.boolean().optional(),
	alignment: z.enum(['left', 'center', 'right']),
	// Generic category, not a typeface: the embedded font isn't web-available.
	fontFamily: z.enum(['sans-serif', 'serif', 'monospace']).optional(),
});

// A styled sub-span of a paragraph, so styling can vary within one text element.
const textRunSchema = z.object({
	text: z.string(),
	bold: z.boolean().optional(),
	italic: z.boolean().optional(),
	underline: z.boolean().optional(),
	color: z
		.string()
		.regex(/^#[0-9a-fA-F]{6}$/)
		.optional(),
});
export type DocumentTemplateTextRun = z.infer<typeof textRunSchema>;

const baseElementSchema = z.object({
	id: z.string().min(1),
	pageIndex: z.number().int().nonnegative(),
	x: z.number(),
	y: z.number(),
	width: z.number().positive(),
	height: z.number().positive(),
});

const textElementSchema = baseElementSchema.extend({
	type: z.literal(DocumentTemplateElementType.TEXT),
	// Optional only because legacy rows have `text` instead; normalize guarantees
	// every TEXT element leaves with a non-empty `content`.
	content: z.array(textRunSchema).optional(),
	text: z.string().optional(),
	style: textStyleSchema,
	// Absent/true hugs the text; false honours `width` and lets the text reflow.
	autoWidth: z.boolean().optional(),
	overflow: z.enum(['expand', 'shrink', 'clip']).optional(),
});

const smartFieldElementSchema = baseElementSchema.extend({
	type: z.literal(DocumentTemplateElementType.SMART_FIELD),
	liquidExpression: z.string().min(1),
	label: z.string(),
	style: textStyleSchema,
});

const imageElementSchema = baseElementSchema.extend({
	type: z.literal(DocumentTemplateElementType.IMAGE),
	// Data URL or remote URL. PDF import inlines rasters, so it needs no asset store.
	src: z.string().min(1),
});

const shapeElementSchema = baseElementSchema.extend({
	type: z.literal(DocumentTemplateElementType.SHAPE),
	shape: z.nativeEnum(DocumentTemplateShapeType),
	color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
	// Absent = transparent.
	fillColor: z
		.string()
		.regex(/^#[0-9a-fA-F]{6}$/)
		.optional(),
});

// Legacy. Child x/y are relative to the REPEAT block's origin. No nesting.
const repeatChildSchema = z.discriminatedUnion('type', [
	textElementSchema,
	imageElementSchema,
	shapeElementSchema,
]);

const repeatElementSchema = baseElementSchema.extend({
	type: z.literal(DocumentTemplateElementType.REPEAT),
	// Dotted path to the array this repeats over, e.g. "holdings".
	arrayPath: z.string().min(1),
	rowHeightMm: z.number().positive(),
	// 'data' is rejected because it would shadow the render scope's own `data` key.
	itemVar: z
		.string()
		.min(1)
		.refine((value) => value !== 'data', {
			message: 'itemVar cannot be "data" (reserved)',
		})
		.optional(),
	children: z.array(repeatChildSchema),
});

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

const tableCellSchema = z.object({
	content: z.array(textRunSchema),
	// Span in grid slots; absent = 1. Resolved by documentLayout/tableGrid.
	colSpan: z.number().int().min(1).optional(),
	rowSpan: z.number().int().min(1).optional(),
	// Horizontal alignment comes from `style.alignment`, not a separate field.
	style: textStyleSchema,
	valign: z.enum(['top', 'middle', 'bottom']).optional(),
	// Fill only; borders are table-level (borderWidthMm/borderColor).
	bg: hexColor.optional(),
});

// `static` rows are authored verbatim; `repeat` rows render one cell-set per
// array item. This supersedes the standalone REPEAT element, which normalize migrates.
const tableStaticRowSchema = z.object({
	kind: z.literal('static'),
	// Repeated at the top of each page the table spans.
	isHeader: z.boolean().optional(),
	minHeightMm: z.number().nonnegative().optional(),
	cells: z.array(tableCellSchema),
});

const tableRepeatRowSchema = z.object({
	kind: z.literal('repeat'),
	arrayPath: z.string().min(1),
	// Same reserved-'data' guard as repeatElementSchema.
	itemVar: z
		.string()
		.min(1)
		.refine((value) => value !== 'data', {
			message: 'itemVar cannot be "data" (reserved)',
		})
		.optional(),
	minHeightMm: z.number().nonnegative().optional(),
	cells: z.array(tableCellSchema),
});

const tableRowSchema = z.discriminatedUnion('kind', [tableStaticRowSchema, tableRepeatRowSchema]);

const tableElementSchema = baseElementSchema.extend({
	type: z.literal(DocumentTemplateElementType.TABLE),
	columns: z.array(z.object({ widthMm: z.number().positive() })).min(1),
	rows: z.array(tableRowSchema).min(1),
	borderWidthMm: z.number().nonnegative().optional(),
	borderColor: hexColor.optional(),
	cellPaddingMm: z.number().nonnegative().optional(),
});

const elementSchema = z.discriminatedUnion('type', [
	textElementSchema,
	smartFieldElementSchema,
	imageElementSchema,
	shapeElementSchema,
	repeatElementSchema,
	tableElementSchema,
]);

const pageSchema = z.object({
	widthMm: z.number().positive(),
	heightMm: z.number().positive(),
	// Absent = 0. Drawn as a dashed guide in the editor; advisory, not enforced.
	marginTopMm: z.number().nonnegative().optional(),
	marginRightMm: z.number().nonnegative().optional(),
	marginBottomMm: z.number().nonnegative().optional(),
	marginLeftMm: z.number().nonnegative().optional(),
});

export const documentTemplateJsonSchema = z.object({
	version: z.number().int().positive(),
	pages: z.array(pageSchema).min(1),
	elements: z.array(elementSchema),
	// Maps a readable token name to the Liquid expression it resolves to, binding
	// every occurrence at once. See expandFieldBindings.
	fieldBindings: z.record(z.string()).optional(),
});

export type DocumentTemplateTextStyle = z.infer<typeof textStyleSchema>;
export type DocumentTemplateTextElement = z.infer<typeof textElementSchema>;
export type DocumentTemplateSmartFieldElement = z.infer<typeof smartFieldElementSchema>;
export type DocumentTemplateImageElement = z.infer<typeof imageElementSchema>;
export type DocumentTemplateShapeElement = z.infer<typeof shapeElementSchema>;
export type DocumentTemplateRepeatChildElement = z.infer<typeof repeatChildSchema>;
export type DocumentTemplateRepeatElement = z.infer<typeof repeatElementSchema>;
export type DocumentTemplateTableCell = z.infer<typeof tableCellSchema>;
export type DocumentTemplateTableRow = z.infer<typeof tableRowSchema>;
export type DocumentTemplateTableElement = z.infer<typeof tableElementSchema>;
export type DocumentTemplateElement = z.infer<typeof elementSchema>;
export type DocumentTemplatePage = z.infer<typeof pageSchema>;

export type PageMargins = Pick<
	DocumentTemplatePage,
	'marginTopMm' | 'marginRightMm' | 'marginBottomMm' | 'marginLeftMm'
>;
export type DocumentTemplateJson = z.infer<typeof documentTemplateJsonSchema>;

export const parseDocumentTemplateJson = (value: unknown): DocumentTemplateJson =>
	documentTemplateJsonSchema.parse(value);
